import { useCallback, useEffect, useState } from "react";
import { downloadWidget, getLatestWidget, type WidgetLatest } from "@/lib/api";
import { detectDownloadPlatform } from "@/lib/downloadPlatform";

type DesktopOS = "windows" | "macos";

const OS_LABEL: Record<DesktopOS, string> = {
  windows: "Windows",
  macos: "macOS",
};

const BUTTON_CLASS =
  "w-full rounded-full bg-[var(--cs-navy)] px-5 py-3 text-[14px] font-semibold text-white shadow-[0_12px_28px_rgba(10,52,96,0.16)] transition-all duration-200 hover:-translate-y-[1px] hover:bg-[var(--cs-navy-strong)] disabled:cursor-not-allowed disabled:opacity-50";

interface DownloadDesktopAppProps {
  manifest?: WidgetLatest | null;
}

export function DownloadDesktopApp({
  manifest: externalManifest,
}: DownloadDesktopAppProps = {}) {
  const [localLatest, setLocalLatest] = useState<WidgetLatest | null>(null);
  const [busy, setBusy] = useState<DesktopOS | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Use external state if provided, otherwise manage internally
  const latest = externalManifest !== undefined ? externalManifest : localLatest;

  useEffect(() => {
    // Only fetch if we're not getting manifest from parent
    if (externalManifest !== undefined) return;

    let active = true;
    getLatestWidget()
      .then((data) => {
        if (active) setLocalLatest(data);
      })
      .catch(() => {
        /* manifest not published yet — keep the panel hidden */
      });
    return () => {
      active = false;
    };
  }, [externalManifest]);

  const onDownload = useCallback(async (platform: DesktopOS) => {
    setBusy(platform);
    setError(null);
    try {
      const blob = await downloadWidget(platform);
      const ext = platform === "windows" ? "exe" : "dmg";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `carmen-widget.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setError("Download mislukt. Probeer het opnieuw.");
    } finally {
      setBusy(null);
    }
  }, []);

  if (!latest) return null;

  const platform = detectDownloadPlatform(navigator.userAgent, navigator.maxTouchPoints);

  if (platform === "mobile") {
    return (
      <div className="rounded-[22px] border border-[rgba(10,52,96,0.10)] bg-white px-4 py-4 shadow-[0_10px_28px_rgba(10,52,96,0.06)]">
        <div className="mb-2 text-[11px] font-normal uppercase tracking-[0.16em] text-[#5f6670]">
          Desktop-app
        </div>
        <p className="text-[13px] text-[var(--cs-ink-muted)]">
          De desktop-app kan niet op je telefoon worden geïnstalleerd. Open
          <span className="mx-1 font-semibold text-[var(--cs-navy)]">
            askcarmen.eu/download
          </span>
          op je Mac of Windows-pc.
        </p>
      </div>
    );
  }

  const primary = platform;
  const secondary: DesktopOS = primary === "windows" ? "macos" : "windows";

  return (
    <div className="rounded-[22px] border border-[rgba(10,52,96,0.10)] bg-white px-4 py-4 shadow-[0_10px_28px_rgba(10,52,96,0.06)]">
      <div className="mb-2 text-[11px] font-normal uppercase tracking-[0.16em] text-[#5f6670]">
        Desktop-app
      </div>
      <p className="mb-3 text-[13px] text-[var(--cs-ink-muted)]">
        Carmen v{latest.version} voor je computer.
      </p>
      <button
        type="button"
        className={BUTTON_CLASS}
        disabled={busy !== null}
        onClick={() => void onDownload(primary)}
      >
        {busy === primary ? "Downloaden…" : `Download voor ${OS_LABEL[primary]}`}
      </button>
      <button
        type="button"
        className="mt-2 w-full text-[12px] font-medium text-[var(--cs-navy)] underline-offset-2 hover:underline disabled:opacity-50"
        disabled={busy !== null}
        onClick={() => void onDownload(secondary)}
      >
        {busy === secondary ? "Downloaden…" : `Ook voor ${OS_LABEL[secondary]}`}
      </button>
      {error ? <p className="mt-2 text-[12px] text-rose-700">{error}</p> : null}
    </div>
  );
}
